import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Alert, Badge, Button, Card, Col, Modal, Row, Spinner } from 'react-bootstrap'
import { useNavigate, useParams } from 'react-router-dom'
import { QRCodeCanvas } from 'qrcode.react'
import { generatePdfToBlob } from '../cetak/draft-pdf/GeneratePdfToBlob'
import { FaCheck, FaFileCircleCheck, FaRotate } from 'react-icons/fa6'
import { FaEdit } from 'react-icons/fa'
import TransaksiService from '../../services/transaksi/TransaksiService'
import PbUmkuService from '../../services/pbumku/PbumkuService'
import MasterService from '../../services/master/MasterService'
import UploadService from '../../services/transaksi/UploadService'
import { formatDateString, getCurrentDateTimeFormattedOnly } from '../../utilities/Utils'

export default function SmkhpApprovalPage() {
    const { nomorAju } = useParams()
    const transaksiService = useMemo(() => new TransaksiService(), [])
    const pbumkuService = useMemo(() => new PbUmkuService(), [])
    const masterService = useMemo(() => new MasterService(), [])
    const uploadService = useMemo(() => new UploadService(), [])
    const navigate = useNavigate()
    const qrRef = useRef(null)
    const [data, setData] = useState(null)
    const [loading, setLoading] = useState(true)
    const [pdfLoading, setPdfLoading] = useState(false)
    const [approving, setApproving] = useState(false)
    const [error, setError] = useState('')
    const [success, setSuccess] = useState('')
    const [previewUrl, setPreviewUrl] = useState('')
    const [previewKey, setPreviewKey] = useState(0)
    const [showConfirm, setShowConfirm] = useState(false)

    const [updatingCetakan, setUpdatingCetakan] = useState(false)

    const decodedNomorAju = decodeURIComponent(nomorAju || '')
    const [qrValue, setQrValue] = useState(`${window.location.origin}/view-smkhp/${encodeURIComponent(decodedNomorAju)}`)

    const loadApproval = async () => {
        setLoading(true)
        setError('')
        try {
            const res = await transaksiService.getSmkhpApproval(decodedNomorAju)
            setData(res.data)
        } catch (e) {
            setError('Dokumen tidak dapat dimuat atau Anda tidak memiliki akses.')
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        loadApproval()
        return () => {
            if (previewUrl) URL.revokeObjectURL(previewUrl)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [nomorAju])

    useEffect(() => {
        if (!data || data.status !== '027') return

        let cancelled = false
        const buildPreview = async () => {
            setPdfLoading(true)
            setError('')
            try {
                const penugasanRes = await transaksiService.listPenugasanAll(decodedNomorAju)
                const disposisiDraft = (penugasanRes?.data?.data || []).find(x => Number(x.idDisposisi) === 9)
                if (!disposisiDraft?.noTugas) {
                    throw new Error('Nomor tugas draft SMKHP belum tersedia.')
                }

                // QR hanya untuk preview draft; PDF resmi akan dibuat ulang setelah approval.
                const qrText = `${window.location.origin}/view-smkhp/${encodeURIComponent(decodedNomorAju)}`
                const canvas = qrRef.current?.querySelector('canvas')
                const qrImage = canvas ? canvas.toDataURL('image/png') : null
                const blob = await generatePdfToBlob({
                    datappm: data,
                    noTugas: disposisiDraft.noTugas,
                    qrImage,
                    finalFor: '1'
                })
                if (cancelled) return
                const nextUrl = URL.createObjectURL(blob)
                setPreviewUrl(prev => {
                    if (prev) URL.revokeObjectURL(prev)
                    return nextUrl
                })
                setPreviewKey(prev => prev + 1)
            } catch (e) {
                if (!cancelled) setError(e?.message || 'Draft PDF belum dapat dibuat untuk ditinjau.')
            } finally {
                if (!cancelled) setPdfLoading(false)
            }
        }

        // QRCodeCanvas perlu satu render sebelum canvas dapat dibaca.
        const timer = setTimeout(buildPreview, 150)
        return () => {
            cancelled = true
            clearTimeout(timer)
        }
    }, [data, decodedNomorAju])

    const editData = () => {
        localStorage.removeItem('nomorAjuStorage')
        localStorage.removeItem('isEditStorage')
        localStorage.setItem('nomorAjuStorage', decodedNomorAju)
        localStorage.setItem('isEditStorage', '1')
        navigate('/permohonan/ekspor/view-2')
    }

    const getQrDataUrl = async (value) => {
        setQrValue(value)
        await new Promise(resolve => setTimeout(resolve, 250))
        const canvas = qrRef.current?.querySelector('canvas')
        return canvas ? canvas.toDataURL('image/png') : null
    }

    // Memperbarui data HC Mutu dan detailnya,
    // kemudian membuat ulang preview PDF secara otomatis.
    const handleMuatUlang = async () => {
        if (!data || data.status !== '027' || updatingCetakan || pdfLoading) {
            return
        }

        setUpdatingCetakan(true)
        setError('')
        setSuccess('')

        try {
            // 1. Ambil data permohonan dan penugasan terbaru.
            const [dataMutuRes, penugasanRes] = await Promise.all([
                transaksiService.getPpmMutuById(decodedNomorAju),
                transaksiService.listPenugasanAll(decodedNomorAju)
            ])

            const dataMutu = dataMutuRes?.data

            if (!dataMutu) {
                throw new Error('Data mutu permohonan tidak ditemukan.')
            }

            // 2. Cari penugasan draft SMKHP.
            const disposisiDraft = (penugasanRes?.data?.data || []).find(item => Number(item.idDisposisi) === 9)

            if (!disposisiDraft?.noTugas) {
                throw new Error('Penugasan draft SMKHP belum tersedia.')
            }

            // 3. Ambil data HC Mutu yang akan diperbarui.
            const hcMutuRes = await transaksiService.getHcMutuById({
                nomorAju: decodedNomorAju,
                noProses: disposisiDraft.noTugas
            })

            if (!hcMutuRes?.data) {
                throw new Error('Data HC Mutu draft SMKHP tidak ditemukan.')
            }

            // 4. Siapkan data HC Mutu terbaru.
            const dataHcMutuUpd = {
                ...hcMutuRes.data,
                nmPengirim: data.namaTrader,
                alPengirim: data.alamatTrader,
                kdposPengirim: data.kodeposTrader,
                telpPengirim: '-',

                nmPenerima: data.namaPartner,
                alPenerima: data.alamatPartner,
                kdposPenerima: '-',
                telpPenerima: '-',

                noRegUpi: dataMutu.upiRegNo,
                nmUpi: dataMutu.upiNama,
                alUpi: dataMutu.upiAlamat,
                telpUpi: dataMutu.upiTelp,

                deskripsiProduk: dataMutu.mutuDesk,
                spesiesProduk: dataMutu.mutuJnsProduk,

                negaraAsalProduk: dataMutu.mutuNegAsalProduk ?? '-',
                wilayahAsalProduk: dataMutu.mutuNegAsalProduk ?? '-',

                culturedArea: dataMutu.mutuArea,
                catchArea: dataMutu.mutuTangkapArea,
                kapalTangkap: dataMutu.mutuNamaKapal,

                processingType: dataMutu.mutuProses,
                temperatureUraian: dataMutu.mutuTemp,
                ktgrTemperature: dataMutu.mutuTempTipe,

                konsumsi: data.peruntukan,
                netto: data.netto ?? 0,
                bruto: data.bruto ?? 0,

                nmJnsTrans: data.angkut ?? '-',
                jnsTrans: data.moda ?? '-',
                nmTrans: data.angkut ?? '-',
                noFlight: data.voyage ?? '-',
                nmTransLain: data.angkut ?? '-',

                tglBerangkat: formatDateString(data.tanggalTibaBerangkat),

                nmNgrTujuan: data.negaraPartner,
                kdNgrTujuan: data.negaraPartner,

                pelAsal: data.pelabuhanMuat,
                pelTujuan: data.pelabuhanBongkar,

                addInfo: dataMutu.addInfo ?? '-',
                kotaTtd: data.tempatTtd ?? '-'
            }

            // 5. Simpan pembaruan HC Mutu.
            const saveHcRes = await transaksiService.addHcMutu(dataHcMutuUpd)
            if (String(saveHcRes?.data?.kode) !== '200') {
                throw new Error(saveHcRes?.data?.keterangan || 'Data HC Mutu gagal diperbarui.')
            }

            // 6. Ambil seluruh detail permohonan.
            const ppmDetilRes = await transaksiService.listPpmDetilAll(decodedNomorAju)

            const details = ppmDetilRes?.data?.data || []

            // Promise.all memastikan seluruh detail selesai diproses
            // sebelum preview PDF dibuat ulang.
            await Promise.all(
                details.map(async (detil) => {
                    const hcMutuDetilPK = {
                        nomorAju: detil.trPpkDtlPK.nomorAju,
                        noUrut: detil.trPpkDtlPK.seri
                    }

                    // Ambil detail HC Mutu yang akan diperbarui.
                    // const hcMutuDetilRes = await transaksiService.getHcMutuDetailById(hcMutuDetilPK)
                    // if (!hcMutuDetilRes?.data) {
                    //     // throw new Error(`Detail HC Mutu nomor urut ${hcMutuDetilPK.noUrut} tidak ditemukan.`)
                    // }

                    // Siapkan detail cetakan terbaru.
                    const dataHcMutuDetilUpd = {
                        // ...hcMutuDetilRes?.data,
                        trHcMutuDtlPK : {
                            nomorAju: detil.trPpkDtlPK.nomorAju,
                            noUrut: detil.trPpkDtlPK.seri
                        },
                        nmLatin: detil.namaLatin,

                        dtlNoReg: dataMutu.upiRegNo ?? '-',
                        dtlNmUpi: dataMutu.upiNama ?? '-',

                        dtlJmlKms: detil.jumlah ?? 0,
                        dtlJnsKms: detil.kodeSatuan ?? '-',
                        dtlNetto: detil.netto ?? 0
                    }

                    // Simpan detail HC Mutu.
                    const saveDetailRes = await transaksiService.addHcMutuDetail(dataHcMutuDetilUpd)

                    // Jika service menggunakan kode respons,
                    // pastikan kegagalan penyimpanan tidak terabaikan.
                    if (saveDetailRes?.data?.kode && String(saveDetailRes.data.kode) !== '200') {
                        // throw new Error(`Detail HC Mutu nomor urut ${hcMutuDetilPK.noUrut} gagal diperbarui.`)
                    }
                })
            )

            // 7. Picu pembuatan ulang preview PDF.
            // Perubahan referensi object data akan menjalankan useEffect
            // pembuat preview yang sudah ada.
            setData(prev => ({ ...prev }))

            setSuccess('Data cetakan berhasil diperbarui. Preview PDF sedang dibuat ulang.')

        } catch (e) {
            console.error('Gagal memperbarui cetakan SMKHP:', e)

            setError(
                e?.response?.data?.message ||
                e?.response?.data?.keterangan ||
                e?.message || 'Gagal memperbarui data cetakan SMKHP.'
            )
        } finally {
            setUpdatingCetakan(false)
        }
    }

    const approve = async () => {
        if (!data || data.status !== '027' || !previewUrl) return
        setApproving(true)
        setError('')
        setSuccess('')
        try {
            // 1. Ubah status 027 -> 028. Signature workflow akan dibuat
            //    setelah PDF resmi disimpan melalui /save-hc-file.
            await transaksiService.approveSmkhp(decodedNomorAju)

            // 2. Ambil assignment disposisi 9 yang menjadi signer Approval.
            const penugasanRes = await transaksiService.listPenugasanAll(decodedNomorAju)
            const signerAssignment = (penugasanRes?.data?.data || []).find(
                x => Number(x.idDisposisi) === 9 && x.noTugas
            )
            if (!signerAssignment?.noTugas) {
                throw new Error('Penugasan penandatangan Approval (disposisi 9) belum tersedia.')
            }

            // 3. Sinkronkan data HC Mutu seperti flow Setuju existing.
            const dataMutuRes = await transaksiService.getPpmMutuById(decodedNomorAju)
            const hcMutuRes = await transaksiService.getHcMutuById({
                nomorAju: decodedNomorAju,
                noProses: signerAssignment.noTugas
            })
            if (!hcMutuRes?.data) {
                throw new Error('Data HC Mutu belum tersedia sehingga PDF resmi belum dapat dibuat.')
            }

            let hcMutu = {
                ...hcMutuRes.data,
                nmPengirim: data.namaTrader,
                alPengirim: data.alamatTrader,
                kdposPengirim: data.kodeposTrader,
                telpPengirim: '-',
                nmPenerima: data.namaPartner,
                alPenerima: data.alamatPartner,
                kdposPenerima: '-',
                telpPenerima: '-',
                noRegUpi: dataMutuRes?.data?.upiRegNo,
                nmUpi: dataMutuRes?.data?.upiNama,
                alUpi: dataMutuRes?.data?.upiAlamat,
                telpUpi: dataMutuRes?.data?.upiTelp,
                deskripsiProduk: dataMutuRes?.data?.mutuDesk,
                spesiesProduk: dataMutuRes?.data?.mutuJnsProduk,
                negaraAsalProduk: dataMutuRes?.data?.mutuNegAsalProduk ?? '-',
                wilayahAsalProduk: dataMutuRes?.data?.mutuNegAsalProduk ?? '-',
                culturedArea: dataMutuRes?.data?.mutuArea,
                catchArea: dataMutuRes?.data?.mutuTangkapArea,
                kapalTangkap: dataMutuRes?.data?.mutuNamaKapal,
                processingType: dataMutuRes?.data?.mutuProses,
                temperatureUraian: dataMutuRes?.data?.mutuTemp,
                ktgrTemperature: dataMutuRes?.data?.mutuTempTipe,
                konsumsi: data.peruntukan,
                netto: data.netto !== null ? data.netto : 0,
                bruto: data.bruto !== null ? data.bruto : 0,
                nmJnsTrans: data.angkut !== null ? data.angkut : '-',
                jnsTrans: data.moda !== null ? data.moda : '-',
                nmTrans: data.angkut !== null ? data.angkut : '-',
                noFlight: data.voyage !== null ? data.voyage : '-',
                nmTransLain: data.angkut !== null ? data.angkut : '-',
                tglBerangkat: formatDateString(data.tanggalTibaBerangkat),
                nmNgrTujuan: data.negaraPartner,
                kdNgrTujuan: data.negaraPartner,
                pelAsal: data.pelabuhanMuat,
                pelTujuan: data.pelabuhanBongkar,
                addInfo: dataMutuRes?.data?.addInfo ?? '-',
                kotaTtd: data.tempatTtd !== null ? data.tempatTtd : '-'
            }

            const countryRes = await masterService.getNegaraById(data.negaraPartner)
            const nomorSeriRes = await pbumkuService.ambilNomorService(
                'FORM_' + countryRes?.data?.kdHcm,
                data.kdUpt,
                new Date().getFullYear(),
                localStorage.getItem('username')
            )
            const noHcMutuExt = nomorSeriRes?.data?.uraianPusat + nomorSeriRes?.data?.nomorUrut
            hcMutu = { ...hcMutu, noHcMutuExt: noHcMutuExt }
            await transaksiService.addHcMutu(hcMutu)

            // 4. Buat PDF resmi Approval, upload, lalu save-hc-file.
            //    save-hc-file adalah boundary yang memicu SignatureWorkflowService.
            const filename = 'SMKHP-' + decodedNomorAju + '-' + getCurrentDateTimeFormattedOnly() + '.pdf'
            const dokPath = process.env.REACT_APP_API_URL_FILE + '/view/' + decodedNomorAju + '/' + filename
            const qrImage = await getQrDataUrl(dokPath)
            const pdfBlob = await generatePdfToBlob({
                datappm: data,
                noTugas: signerAssignment.noTugas,
                qrImage,
                finalFor: '1'
            })
            const formData = new FormData()
            formData.append('file', pdfBlob, filename)
            const uploadRes = await uploadService.uploadFileFormData(decodedNomorAju, formData)
            if (uploadRes?.status !== 200) {
                throw new Error('Upload PDF Approval gagal.')
            }

            const saveFileRes = await transaksiService.saveSmkhpFile({
                nomorAju: decodedNomorAju,
                noHcMutu: hcMutu.noHcMutu,
                noSeriMutuElectronic: noHcMutuExt,
                finalFor: '1',
                dokPath,
                dokName: filename,
                createdBy: localStorage.getItem('userId'),
                createdDate: new Date()
            })
            if (saveFileRes?.data?.kode && saveFileRes.data.kode !== '200') {
                throw new Error(saveFileRes.data.keterangan || 'Gagal menyimpan file SMKHP.')
            }

            setSuccess('Draft SMKHP berhasil disetujui. PDF Approval sudah disimpan dan proses tanda tangan telah diteruskan ke penandatangan.')
            setData(prev => ({ ...prev, status: '028' }))
            setPreviewUrl('')
            setShowConfirm(false)
        } catch (e) {
            setError(e?.response?.data?.message || e?.response?.data?.keterangan || e?.message || 'Dokumen gagal disetujui/diproses.')
        } finally {
            setApproving(false)
        }
    }

    if (loading) return <div className="text-center py-5"><Spinner animation="border" /><div className="mt-2">Memuat draft SMKHP...</div></div>

    return <div className="container-fluid py-4">
        <Row className="justify-content-center">
            <Col xl={11}>
                <Card className="border-0 shadow-sm">
                    <Card.Body>
                        <div className="d-flex align-items-center justify-content-between mb-4">
                            <div>
                                <h3 className="mb-1">Persetujuan Draft SMKHP</h3>
                                <div className="text-muted">Periksa draft PDF dengan teliti sebelum memberikan persetujuan.</div>
                            </div>
                            {data?.status === '027'
                                ? <Badge bg="warning" text="dark">Menunggu Persetujuan</Badge>
                                : <Badge bg="success">Sudah Disetujui</Badge>}
                        </div>

                        {error && <Alert variant="danger">{error}</Alert>}
                        {success && <Alert variant="success">{success}</Alert>}

                        <Row className="g-3 mb-4">
                            <Col md={4}><div className="small text-muted">Nomor Aju</div><div className="fw-semibold">{data?.nomorAju || '-'}</div></Col>
                            <Col md={4}><div className="small text-muted">Nama Pengguna Jasa</div><div className="fw-semibold">{data?.namaTrader || '-'}</div></Col>
                            <Col md={4}><div className="small text-muted">NPWP</div><div className="fw-semibold">{data?.npwpTrader || '-'}</div></Col>
                            <Col md={4}><div className="small text-muted">Negara Tujuan</div><div className="fw-semibold">{data?.negaraPartner || '-'}</div></Col>
                            <Col md={4}><div className="small text-muted">Pelabuhan Tujuan</div><div className="fw-semibold">{data?.pelabuhanBongkar || '-'}</div></Col>
                            <Col md={4}><div className="small text-muted">Partner</div><div className="fw-semibold">{data?.namaPartner || '-'}</div></Col>
                        </Row>

                        <Alert variant="info">
                            <FaFileCircleCheck className="me-2" />
                            Ini adalah <strong>draft PDF SMKHP</strong>. Jika data belum benar, lakukan perubahan data terlebih dahulu. Setelah data benar dan draft sudah sesuai, klik <strong>Setujui Draft SMKHP</strong>.
                        </Alert>

                        <Card className="border mb-4">
                            <Card.Header className="d-flex justify-content-between align-items-center">
                                <strong>Preview Draft PDF SMKHP</strong>
                                {/* {data?.status === '027' && <Button size="sm" variant="outline-secondary" onClick={() => setData(prev => ({ ...prev }))} disabled={pdfLoading}><FaRotate className="me-1" /> Muat Ulang</Button>} */}
                            </Card.Header>
                            <Card.Body className="p-2">
                                <div ref={qrRef} style={{ display: 'none' }}><QRCodeCanvas value={qrValue} size={100} /></div>
                                {pdfLoading
                                    ? <div className="text-center py-5"><Spinner animation="border" /><div className="mt-2">Membuat preview draft PDF...</div></div>
                                    : previewUrl
                                        ? <iframe key={previewKey} title="Preview Draft PDF SMKHP" src={previewUrl} style={{ width: '100%', height: '75vh', border: '1px solid #ddd' }} />
                                        : <Alert variant="warning" className="m-3">Preview draft PDF belum tersedia.</Alert>}
                            </Card.Body>
                        </Card>

                        <div className="d-flex justify-content-between gap-2 flex-wrap">
                            <Button variant="secondary" onClick={() => navigate('/notifications')}>Kembali</Button>
                            {data?.status === '027' && <div className="d-flex gap-2">
                                <Button variant="warning" onClick={editData}><FaEdit className="me-2" />Ubah Data Permohonan</Button>
                                <Button variant="info" onClick={handleMuatUlang} disabled={updatingCetakan || pdfLoading || approving}>
                                    {updatingCetakan || pdfLoading ? (
                                        <>
                                            <Spinner size="sm" animation="border" className="me-2" />
                                            {updatingCetakan ? 'Memperbarui Cetakan...' : 'Membuat Preview...' }
                                        </>
                                    ) : (
                                        <>
                                            <FaRotate className="me-2" />
                                            Update Cetakan
                                        </>
                                    )}
                                </Button>
                                <Button variant="primary" disabled={approving || pdfLoading || !previewUrl} onClick={() => setShowConfirm(true)}>
                                    {approving ? <Spinner size="sm" animation="border" className="me-2" /> : <FaCheck className="me-2" />}
                                    Setujui Draft SMKHP
                                </Button>
                            </div>}
                        </div>

                        <Modal show={showConfirm} onHide={() => !approving && setShowConfirm(false)} centered backdrop="static">
                            <Modal.Header closeButton={!approving}>
                                <Modal.Title>Konfirmasi Persetujuan</Modal.Title>
                            </Modal.Header>
                            <Modal.Body>
                                <p className="mb-2">Anda akan menyetujui draft PDF SMKHP berikut:</p>
                                <div className="fw-semibold mb-3">{decodedNomorAju}</div>
                                <Alert variant="warning" className="mb-0">
                                    Pastikan seluruh data dan isi PDF sudah benar. Setelah disetujui, sistem akan membuat PDF Approval dan meneruskannya ke proses tanda tangan elektronik.
                                </Alert>
                            </Modal.Body>
                            <Modal.Footer>
                                <Button variant="secondary" onClick={() => setShowConfirm(false)} disabled={approving}>Batal</Button>
                                <Button variant="primary" onClick={approve} disabled={approving}>
                                    {approving ? <><Spinner size="sm" animation="border" className="me-2" />Memproses...</> : 'Ya, Setujui Draft'}
                                </Button>
                            </Modal.Footer>
                        </Modal>
                    </Card.Body>
                </Card>
            </Col>
        </Row>
    </div>
}
